import React, {useContext, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useForm} from "react-hook-form";
import {handleDefaultValues} from "../../common/Utils";
import {UserContext} from "../../../lib/UserProvider";
import {submitPublicWaiver} from "../../common/api/PortalCustomerApi";
import {CONSTANTS} from "../../common/constants";
import {AppContext} from "../../../lib/AppContext";
import MDBox from "../../../components/MDBox";
import MDButton from "../../../components/MDButton";
import {CartContext} from "../../../lib/CartProvider";
import {CardBody, CardTitle} from "react-bootstrap";
import {Card} from "@mui/material";
import EsignatureField from "./EsignatureField";

export default function WaiverBody({waiver, callback}) {
  const [userContext, setUserContext] = useContext(UserContext);
  const [user, setUser] = useState();
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useContext(AppContext);
  const [cartContext, setCartContext] = useContext(CartContext);

  useEffect(() => {
    if (userContext?.sessionUser) {
      setUser(userContext?.sessionUser);
    }
  }, [userContext]);
  const [formInvalid, setFormInvalid] = useState(false);
  const {register, handleSubmit, setValue, control} = useForm({
    values: handleDefaultValues(user),
  });

  const [waiverResponse, setWaiverResponse] = useState();
  const formatReplacements = (html, replacement, placeholder, type) => {
    let _html = html;
    const pattern = '{' + replacement + '}';

    const regex = new RegExp(pattern, 'g');

    let matches = _html.match(regex);

    if (!matches) {
      return _html;
    }

    matches.forEach((item, index) => {
      let isCheck = replacement.match('checkbox');
      let defaultValue = isCheck ? 'Yes' : '';
      let styles = isCheck ? "position:relative;top:5px;" : "";
      let isRequired = true;
      if (item.match('conditional')) {
        isRequired = false;
      }
      let classNames = isCheck ? "replacement form-check d-inline w-auto" : "replacement form-control d-inline w-auto";
      if ('esignature' === type) {
        _html = _html.replace(item, '<div class="esignature-mount" data-esig-mount="' + replacement + '-' + index
          + '" data-esig-required="' + (isRequired ? '1' : '0') + '"></div>');
      } else  if ('textarea' === type) {
        _html = _html.replace(item, '<textarea required value="' + defaultValue + '" placeholder="'
          + placeholder + '" type="' + type + '" name="' + replacement + '-' + index + '" class="replacement form-control d-block auto" rows="4"></textarea>');
      } else {
        if (isRequired) {
          _html = _html.replace(item, '<input required value="' + defaultValue + '" style="' + styles + '" placeholder="'
            + placeholder + '" type="' + type + '" name="' + replacement + '-' + index + '" class="' + classNames + '" />');
        } else {
          _html = _html.replace(item, '<input onchange="this.value = this.checked ? \'Yes\' : \'No\';" value="No" style="' + styles + '" placeholder="'
            + placeholder + '" type="' + type + '" name="' + replacement + '-' + index + '" class="replacement" />');
        }
      }
    });

    return _html;
  }

  const formItems = [
    {icon: "person", label: "First Name", replacement: "firstName", type: "input"},
    {icon: "person", label: "Last Name", replacement: "lastName", type: "input"},
    {icon: "person", label: "Full Name", replacement: "fullName", type: "input"},
    {icon: "person", label: "Parent / Guardian Name", replacement: "legal-name", type: "input"},
    {icon: "person_add", label: "Child", replacement: "childName", type: "input"},
    {icon: "person_add", label: "Child", replacement: "child-name", type: "input"},
    {icon: "check_box", label: "Agree", replacement: "checkbox-agree", type: "checkbox"},
    {icon: "check_box_outline_blank", label: "Opt-In", replacement: "checkbox-conditional", type: "checkbox"},
    {icon: "app_registration", label: "E-Signature", replacement: "e-signature", type: "esignature"},
    {icon: "notes", label: "Answer", replacement: "answer", type: "input"},
    {icon: "edit", label: "Signature", replacement: "signature", type: "text"},
    {icon: "event", label: "Date", replacement: "date", type: "date"},
    {icon: "event_note", label: "Comment", replacement: "message", type: "textarea"},
    {icon: "event", label: "Current Date", replacement: "currentDate", type: "date"},
  ];
  const toForm = (html) => {
    let _html = html;
    for (let j in formItems) {
      let item = formItems[j];
      _html = formatReplacements(_html, item.replacement, item.label, item.type);
    }
    return _html;
  }

  const formContainerRef = useRef(null);
  const esigRefs = useRef({});
  const [esigMounts, setEsigMounts] = useState([]);

  useEffect(() => {
    const container = formContainerRef.current;
    if (!container) {
      setEsigMounts([]);
      return;
    }
    const nodes = Array.from(container.querySelectorAll('[data-esig-mount]'));
    esigRefs.current = {};
    setEsigMounts(nodes.map((node) => ({
      name: node.dataset.esigMount,
      required: node.dataset.esigRequired === '1',
      node,
    })));
  }, [waiver?.waiverBody]);

  const handleWaiverSubmit = async (e) => {

    for (const mount of esigMounts) {
      const field = esigRefs.current[mount.name];
      if (mount.required && field?.isEmpty()) {
        field.showError();
        setContext({...context, error: {message: 'Please provide your signature before submitting.'}});
        return;
      }
    }

    setSaving(true);
    const formData = {};
    const form = document.getElementById('waiverForm');

    const elements = form.elements;
    for (let j in elements) {
      if (!elements[j].className?.match('replacement')) {
        continue;
      }
      if (!elements[j].value) {
        continue;
      }
      formData[elements[j].name] = elements[j].value;
    }

    const saveData = {
      waiver: waiver,
      formUser: user,
      formData: formData,
    }

    submitPublicWaiver(saveData).then(result => {
      setSaving(false);
      if (result.error) {
        setContext({...context, error: {message: result.message}});
      } else {

        setUserContext({...userContext, refreshIdentity: true})

        setWaiverResponse(result);

        let _c = {...context};
        _c.toast = {message: 'Waiver submitted successfully'};
        setContext({..._c});
        callback(waiver)
        setTimeout(() => {
          setContext({...context, toast: {}});
        }, CONSTANTS.TOAST_TIMEOUT);
      }
    });
  }

  return (
    <>
      {waiver?.waiverBody &&
        <Card>
          <CardTitle>{waiver.waiverName}</CardTitle>
          <CardBody>
            <MDBox mt={3}>
              {waiver?.waiverBody && <form id="waiverForm" onSubmit={(e) => {
                e.preventDefault();
                handleWaiverSubmit(e);
              }}>
                <span
                  ref={formContainerRef}
                  dangerouslySetInnerHTML={{__html: toForm(waiver?.waiverBody)}}></span>
                {esigMounts.map((mount) => createPortal(
                  <EsignatureField
                    key={mount.name}
                    name={mount.name}
                    required={mount.required}
                    ref={(inst) => {
                      if (inst) {
                        esigRefs.current[mount.name] = inst;
                      } else {
                        delete esigRefs.current[mount.name];
                      }
                    }}
                  />,
                  mount.node
                ))}
                <div className="row">
                  <div className="col col-12 align-content-end">
                    <MDButton type="submit" color="info" disabled={saving}>
                      {saving ? 'Saving' : 'Save'}
                    </MDButton>
                  </div>
                </div>
              </form>}
              <hr/>
            </MDBox>
          </CardBody>
        </Card>
      }
    </>
  )

}
